import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  IndicatorPayload,
  type Bar,
  type FundamentalDto,
  type Instrument,
  type IndicatorSnapshotDto,
  type NewsItemDto,
  type Pick,
  type Timeframe,
} from "@alpha/shared";
import { ApiError, api, qs } from "../api";
import { ChartView, type Layers } from "../components/ChartView";
import { InstrumentSearch } from "../components/InstrumentSearch";
import { Chip, Empty, ErrorBox, Loading, PageHeader, fmtCompact, fmtNum, signClass } from "../components/ui";
import { formatDateTime } from "../env";
import { lastValue } from "../lib/chartData";
import { useAsync } from "../useAsync";

const TIMEFRAMES: Timeframe[] = ["1d", "1h", "15m"];

const LAYER_LABELS: [keyof Layers, string][] = [
  ["sma50", "SMA 50"],
  ["sma200", "SMA 200"],
  ["ema21", "EMA 21"],
  ["bbands", "Bollinger"],
  ["rsi", "RSI"],
  ["macd", "MACD"],
  ["picks", "Pick lines"],
];

/** A 404 from an optional resource (no snapshot yet, no fundamentals) is an empty result, not an error. */
async function optional<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

export function ChartPage() {
  const [params, setParams] = useSearchParams();
  const instrumentId = params.get("instrument");
  const tfParam = params.get("tf");
  const tf: Timeframe = TIMEFRAMES.includes(tfParam as Timeframe) ? (tfParam as Timeframe) : "1d";
  const [layers, setLayers] = useState<Layers>({ sma50: true, sma200: true, ema21: true, bbands: false, rsi: true, macd: true, picks: true });

  const pick = (i: Instrument) => setParams({ instrument: i.id, tf });

  return (
    <section>
      <PageHeader title="Chart">
        <InstrumentSearch onSelect={pick} placeholder="Ticker or name" label="Chart ticker search" />
        <div className="seg" role="group" aria-label="Timeframe">
          {TIMEFRAMES.map((t) => (
            <button
              key={t}
              type="button"
              className={t === tf ? "active" : ""}
              aria-pressed={t === tf}
              onClick={() => setParams(instrumentId ? { instrument: instrumentId, tf: t } : { tf: t })}
            >
              {t}
            </button>
          ))}
        </div>
      </PageHeader>
      {instrumentId ? (
        <ChartBody key={instrumentId} instrumentId={instrumentId} tf={tf} layers={layers} setLayers={setLayers} />
      ) : (
        <div className="card">
          <Empty>Search for a ticker to load its chart. Seeded demo tickers start with DEM.</Empty>
          <p className="attribution muted">
            Charting by{" "}
            <a href="https://www.tradingview.com/" target="_blank" rel="noopener noreferrer">
              TradingView Lightweight Charts
            </a>
          </p>
        </div>
      )}
    </section>
  );
}

function ChartBody({
  instrumentId,
  tf,
  layers,
  setLayers,
}: {
  instrumentId: string;
  tf: Timeframe;
  layers: Layers;
  setLayers: (l: Layers) => void;
}) {
  const inst = useAsync((s) => api.get<Instrument>(`/v1/instruments/${instrumentId}`, s), [instrumentId]);
  const bars = useAsync(
    (s) => api.get<{ bars: Bar[] }>(`/v1/instruments/${instrumentId}/bars${qs({ timeframe: tf, limit: 1000 })}`, s),
    [instrumentId, tf],
  );
  const snap = useAsync(
    (s) => optional(api.get<IndicatorSnapshotDto>(`/v1/instruments/${instrumentId}/indicators${qs({ timeframe: tf })}`, s)),
    [instrumentId, tf],
  );
  const picks = useAsync((s) => api.get<{ items: Pick[] }>(`/v1/picks${qs({ instrumentId, take: 50 })}`, s), [instrumentId]);
  const fundamentals = useAsync((s) => optional(api.get<FundamentalDto>(`/v1/instruments/${instrumentId}/fundamentals/latest`, s)), [instrumentId]);
  const news = useAsync((s) => api.get<{ items: NewsItemDto[] }>(`/v1/instruments/${instrumentId}/news?take=8`, s), [instrumentId]);

  const payload = useMemo(() => {
    if (!snap.data) return null;
    const parsed = IndicatorPayload.safeParse(snap.data.payload);
    return parsed.success ? parsed.data : null;
  }, [snap.data]);
  const payloadBad = !!snap.data && !payload;
  const openPicks = useMemo(() => (picks.data?.items ?? []).filter((p) => p.status !== "closed"), [picks.data]);

  if (inst.loading) return <Loading />;
  if (inst.error || !inst.data) return <ErrorBox error={inst.error} onRetry={inst.reload} />;

  const barList = bars.data?.bars ?? [];
  const lastBar = barList[barList.length - 1];
  const prevBar = barList[barList.length - 2];
  const change = lastBar && prevBar ? ((Number(lastBar.close) - Number(prevBar.close)) / Number(prevBar.close)) * 100 : null;

  return (
    <div className="split">
      <div className="card grow">
        <div className="row between">
          <div>
            <strong className="big">{inst.data.ticker}</strong> <span className="muted">{inst.data.name}</span>
            {inst.data.sector && <span className="muted"> | {inst.data.sector}</span>}
          </div>
          {lastBar && (
            <div className="num">
              {lastBar.close}
              {change !== null && <span className={signClass(change)}> {change >= 0 ? "+" : ""}{change.toFixed(2)}%</span>}
            </div>
          )}
        </div>
        <div className="row layers" role="group" aria-label="Chart layers">
          {LAYER_LABELS.map(([k, label]) => (
            <label key={k} className="check">
              <input type="checkbox" checked={layers[k]} onChange={(e) => setLayers({ ...layers, [k]: e.target.checked })} />
              {label}
            </label>
          ))}
        </div>
        {bars.loading && <Loading label="Loading bars..." />}
        {bars.error && <ErrorBox error={bars.error} onRetry={bars.reload} />}
        {!bars.loading && !bars.error && barList.length === 0 && <Empty>No {tf} bars stored for this instrument.</Empty>}
        {!bars.loading && !bars.error && barList.length > 0 && (
          <ChartView rangeKey={`${instrumentId}:${tf}`} bars={barList} payload={payload} picks={openPicks} tf={tf} layers={layers} />
        )}
        {snap.error && <ErrorBox error={snap.error} onRetry={snap.reload} />}
        {!snap.loading && !snap.error && !snap.data && <p className="muted">No stored indicators for the {tf} timeframe yet; showing price and volume only.</p>}
        {payloadBad && <p className="warn">The stored indicator payload has an unexpected shape, so overlays are hidden.</p>}
        {picks.error && <ErrorBox error={picks.error} onRetry={picks.reload} />}
      </div>

      <aside className="narrow stack">
        <div className="card">
          <h3>Latest indicators</h3>
          {snap.loading ? (
            <Loading />
          ) : payload ? (
            <dl className="kv">
              <dt>As of</dt>
              <dd className="num">{snap.data ? formatDateTime(snap.data.ts) : ""}</dd>
              <dt>SMA 50</dt>
              <dd className="num">{fmtNum(lastValue(payload.sma50))}</dd>
              <dt>SMA 200</dt>
              <dd className="num">{fmtNum(lastValue(payload.sma200))}</dd>
              <dt>EMA 21</dt>
              <dd className="num">{fmtNum(lastValue(payload.ema21))}</dd>
              <dt>RSI 14</dt>
              <dd className="num">{fmtNum(lastValue(payload.rsi), 1)}</dd>
              <dt>MACD hist</dt>
              <dd className={`num ${signClass(lastValue(payload.macd?.hist))}`}>{fmtNum(lastValue(payload.macd?.hist), 3)}</dd>
              <dt>ADX</dt>
              <dd className="num">{fmtNum(lastValue(payload.adx?.adx), 1)}</dd>
            </dl>
          ) : (
            <Empty>None stored.</Empty>
          )}
        </div>

        <div className="card">
          <h3>Picks on {inst.data.ticker}</h3>
          {picks.loading ? (
            <Loading />
          ) : openPicks.length === 0 ? (
            <Empty>No open picks you can see.</Empty>
          ) : (
            <ul className="plain-list">
              {openPicks.map((p) => (
                <li key={p.id} className="pick-line">
                  <Link to={`/picks/${p.id}`}>
                    {p.direction} <span className="num">{p.entry} / {p.target} / {p.stop}</span>
                  </Link>
                  <Chip value={p.visibility} />
                </li>
              ))}
            </ul>
          )}
          <Link className="btn small" to={`/picks/new?instrument=${instrumentId}`}>
            New pick
          </Link>
        </div>

        <div className="card">
          <h3>Fundamentals</h3>
          {fundamentals.loading ? (
            <Loading />
          ) : fundamentals.error ? (
            <ErrorBox error={fundamentals.error} onRetry={fundamentals.reload} />
          ) : fundamentals.data ? (
            <dl className="kv">
              <dt>Period</dt>
              <dd>
                {fundamentals.data.periodType} to {fundamentals.data.periodEnd}
              </dd>
              {Object.entries(highlights(fundamentals.data.payload))
                .filter(([, v]) => typeof v === "number" || typeof v === "string")
                .slice(0, 12)
                .map(([k, v]) => (
                  <FundRow key={k} k={k} v={v as number | string} />
                ))}
            </dl>
          ) : (
            <Empty>None stored.</Empty>
          )}
        </div>

        <div className="card">
          <h3>News</h3>
          {news.loading ? (
            <Loading />
          ) : news.error ? (
            <ErrorBox error={news.error} onRetry={news.reload} />
          ) : (news.data?.items.length ?? 0) === 0 ? (
            <Empty>No recent headlines.</Empty>
          ) : (
            <ul className="plain-list news">
              {news.data!.items.map((n) => (
                <li key={n.id}>
                  {/^https?:\/\//i.test(n.url) ? (
                    <a href={n.url} target="_blank" rel="noopener noreferrer nofollow">
                      {n.headline}
                    </a>
                  ) : (
                    <span>{n.headline}</span>
                  )}
                  <div className="muted">
                    {n.source}, {formatDateTime(n.publishedAt)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

/** Stored fundamentals keep ratios under `highlights` (EODHD); fall back to the top level for other shapes. */
function highlights(payload: Record<string, unknown>): Record<string, unknown> {
  const h = payload.highlights;
  return h && typeof h === "object" && !Array.isArray(h) ? (h as Record<string, unknown>) : payload;
}

function FundRow({ k, v }: { k: string; v: number | string }) {
  const label = k.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());
  return (
    <>
      <dt>{label}</dt>
      <dd className="num">{typeof v === "number" ? fmtCompact(v) : v}</dd>
    </>
  );
}
